import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Menu, X, Target } from 'lucide-react';

export function Navigation() {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  
  // Mobile optimization
  const prefersReducedMotion = useReducedMotion();
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

  // Optimized scroll detection
  useEffect(() => {
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          const scrollY = window.scrollY;
          setIsScrolled(scrollY > 50);
          ticking = false;
        });
        ticking = true;
      }
    };
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [navigate]);

  return (
    <>
      {/* Mobile Performance Optimization Styles */}
      <style>
        {`
          @media (max-width: 768px) {
            .mobile-sticky-nav {
              position: fixed !important;
              top: 0 !important;
              left: 0 !important;
              right: 0 !important;
              width: 100% !important;
              z-index: 9999 !important;
              -webkit-transform: translate3d(0,0,0) !important;
              transform: translate3d(0,0,0) !important;
              -webkit-backface-visibility: hidden !important;
              backface-visibility: hidden !important;
            }
          }
        `}
      </style>
      
      {/* Navigation */}
      <nav 
        className={`mobile-sticky-nav fixed top-0 left-0 right-0 w-full z-[9999] transition-all duration-300 ${
          isScrolled 
            ? 'bg-black/20 backdrop-blur-xl shadow-xl' 
            : 'bg-black/5 backdrop-blur-lg'
        } ${
          mobileMenuOpen 
            ? 'border-b-0' 
            : 'border-b border-white/10'
        }`}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 9999,
          WebkitTransform: 'translate3d(0,0,0)',
          transform: 'translate3d(0,0,0)',
          backdropFilter: isScrolled ? 'blur(24px)' : 'blur(16px)',
          WebkitBackdropFilter: isScrolled ? 'blur(24px)' : 'blur(16px)'
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4 lg:py-6">
            {/* Logo */}
            <motion.div 
              className="flex-shrink-0"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
            >
              <button 
                onClick={() => navigate('/')}
                className="flex items-center"
              >
                <img 
                  src="/Logo-Rulevia-White.svg" 
                  alt="Rulevia" 
                  className="h-12 w-auto"
                />
              </button>
            </motion.div>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center space-x-6">
              <a 
                href="/#features" 
                className="text-gray-300 hover:text-white transition-colors"
              >
                Features
              </a>
              <a 
                href="/#comparison" 
                className="text-gray-300 hover:text-white transition-colors"
              >
                Results
              </a>
              <button 
                onClick={() => navigate('/blog')}
                className="text-gray-300 hover:text-white transition-colors font-medium"
              >
                Blog
              </button>
              <a 
                href="/#pricing" 
                className="text-gray-300 hover:text-white transition-colors"
              >
                Pricing
              </a>
              {/* Hidden as requested - Try It Free button
              <button 
                onClick={() => navigate('/')}
                className="text-gray-300 hover:text-white transition-colors font-medium flex items-center space-x-2"
              >
                <Target className="w-4 h-4" />
                <span>Try It Free</span>
              </button>
              */}
              <button 
                onClick={() => navigate('/onboarding?mode=login')}
                className="text-gray-300 hover:text-white transition-colors font-medium"
              >
                Login
              </button>
            </div>

            {/* Mobile Menu Button */}
            <button 
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden text-white p-2 relative z-50"
            >
              <motion.div
                animate={{ rotate: mobileMenuOpen ? 90 : 0 }}
                transition={{ duration: 0.2, ease: "easeInOut" }}
              >
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </motion.div>
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      <AnimatePresence mode="wait">
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ 
              duration: 0.2, 
              ease: [0.25, 0.1, 0.25, 1],
              ...(prefersReducedMotion && { duration: 0 })
            }}
            className={`lg:hidden fixed top-[73px] left-0 right-0 w-full z-[9998] border-b border-white/10 transition-all duration-300 ${
              isScrolled 
                ? 'bg-black/20 backdrop-blur-xl shadow-xl' 
                : 'bg-black/5 backdrop-blur-lg'
            }`}
            style={{
              position: 'fixed',
              top: '73px',
              left: 0,
              right: 0,
              zIndex: 9998,
              WebkitTransform: 'translate3d(0,0,0)',
              transform: 'translate3d(0,0,0)',
              backdropFilter: isScrolled ? 'blur(24px)' : 'blur(16px)',
              WebkitBackdropFilter: isScrolled ? 'blur(24px)' : 'blur(16px)'
            }}
          >
            <div className="px-4 py-6">
              <div className="flex flex-col space-y-4">
                {[
                  { href: "/#features", text: "Features" },
                  { href: "/#comparison", text: "Results" },
                  { href: "/blog", text: "Blog", isRoute: true },
                  { href: "/#pricing", text: "Pricing" }
                ].map((item, index) => (
                  <motion.a
                    key={item.href}
                    href={item.href}
                    onClick={(e) => {
                      if (item.isRoute) {
                        e.preventDefault();
                        navigate(item.href);
                      }
                      setMobileMenuOpen(false);
                    }}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ 
                      delay: prefersReducedMotion ? 0 : index * 0.05,
                      duration: 0.2 
                    }}
                    className="text-gray-300 hover:text-white transition-colors py-2 px-3 rounded-lg hover:bg-white/5"
                  >
                    {item.text}
                  </motion.a>
                ))}
                
                {/* Hidden as requested - Try It Free button
                <motion.button 
                  onClick={() => { 
                    navigate('/'); 
                    setMobileMenuOpen(false); 
                  }}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ 
                    delay: prefersReducedMotion ? 0 : 0.15,
                    duration: 0.2 
                  }}
                  className="text-gray-300 hover:text-white transition-colors font-medium text-left py-2 px-3 rounded-lg hover:bg-white/5 flex items-center space-x-2"
                >
                  <Target className="w-4 h-4" />
                  <span>Try It Free</span>
                </motion.button>
                */}
                
                <motion.button 
                  onClick={() => { 
                    navigate('/onboarding?mode=login'); 
                    setMobileMenuOpen(false); 
                  }}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ 
                    delay: prefersReducedMotion ? 0 : 0.2,
                    duration: 0.2 
                  }}
                  className="text-gray-300 hover:text-white transition-colors font-medium text-left py-2 px-3 rounded-lg hover:bg-white/5"
                >
                  Login
                </motion.button>
                
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Spacer to prevent content from going under fixed nav */}
      <div className="h-[73px] lg:h-[88px]" />
    </>
  );
}